import Vue from 'vue';
import axios from 'axios';
import M from 'materialize-css';
import * as Sentry from '@sentry/browser';

axios.defaults.headers.common['X-ApiKey'] = window.env.VUE_APP_TOKEN;

// Ignore alerts for whitelisted endpoints
const whitelist = [
  '/insights/instruments/option/', // Check for when option already exists
  '/insights/instruments/equity/', // Check for when equity already exists
  '/insights/instruments/parent/by_capiq_id/', // Check for when instrument already exists
  '/insights/instruments/search_crypto/' // prevents alert when crypto isn't found in search
];

axios.interceptors.request.use(
  config => config,
  error => Promise.reject(error),
);

// A Slack alert adds nothing for these, the response body already tells the user what is wrong,
// and it repeats for as long as they keep retrying. Sentry still records them.
const selfExplanatoryStatuses = [401, 403, 404];

// CUSTOM ERROR RULES
function getCustomErrorMessage(error, apiUrl) {
  // default error message
  let htmlErrorMessage = `Error fetching data, we've notified ${window.env.SLACK_ALERT_CHANNEL}`;
  let color = 'red';

  const { status, data } = error.response || {};

  // Custom error message for country not found when searching CapIQ trading items
  if (status === 503 && apiUrl.pathname === '/insights/capiq/trading_items/') {
    htmlErrorMessage = data;
    color = 'orange';
  }
  if (data && data.error_message) {
    htmlErrorMessage = data.error_message;
  }

  return [htmlErrorMessage, color];
}

axios.interceptors.response.use(
  response => response,
  error => {
    error.message = `${error.message} ${error.config.method} ${error.config.url}`;
    error.description = `URL: ${error.config.url}|METHOD: ${error.config.method}|DATA: ${error.config.data}|User: ${window.user ? window.user.email : 'Anonymous'}`;
    Sentry.withScope(scope => {
      const apiUrl = new URL(error.config.url);
      scope.setUser(window.user || null);
      scope.setLevel('fatal');
      scope.setTag('request-url', window.location.href);

      if (!whitelist.some(url => error.config.url.includes(url))) {
        Sentry.captureException(error);
        const [htmlErrorMessage, color] = getCustomErrorMessage(error, apiUrl);
        M.toast({
          html: htmlErrorMessage,
          classes: `${color} accent-4`,
        });

        const { status } = error.response || {};
        if (!selfExplanatoryStatuses.includes(status)) {
          const msg = `
*${window.user ? `${window.user.name} (${window.user.email})` : 'Anonymous'}* encountered an error on Fool IQ
>*URL:* ${error.config.url}
>*Method:* ${error.config.method.toUpperCase()}
>*Data:* ${error.config.data || '-'}
          `;
          fetch(`${window.env.APING_URL}/insights/common/slack/`, {
            method: 'POST',
            body: JSON.stringify({
              message: msg,
              channel: window.env.SLACK_ALERT_CHANNEL,
            }),
            headers: {
              'Content-Type': 'application/json',
              'X-ApiKey': window.env.VUE_APP_TOKEN,
            },
          });
        }
      }
    });
    return Promise.reject(error);
  },
);

export default {
  install() {
    Vue.prototype.$axios = axios;
  },
};
